import { Box, TextField, debounce } from '@mui/material'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { SANS_FONT } from '../../theme'
import { OnboardSearchContainer } from '../../pages/eater/onboardPages/styled/OnboardSearch.styled'
import {
  EaterSearchResult,
  fetchEaterSearch,
} from '../../services/searchService'
import { useAppSelector } from '../../store'
import { selectEater } from '../../store/profileSlice'
import { Member } from '../../types/groupTypes'
import { NavbarSearchAutocompleteViewInnerContainer } from '../navBar/styled/SearchStyled'
import {
  UserEmailAutocompleteViewListDetailBox,
  UserEmailAutocompleteViewListDetailImage,
  UserEmailAutocompleteViewListDetailSubTypography,
  UserEmailAutocompleteViewListDetailTypography,
  UserEmailAutocompleteViewMainBox,
  UserEmailAutocompleteViewSecondBox,
  UserEmailOptionDialogChip,
  UserEmailOptionDialogDetailBox,
  UserEmailOptionDialogDetailIcon,
  UserEmailOptionDialogDetailTypography,
  UserEmailOptionDialogMainBox,
  UserEmailSearchResultSearchTermContainer,
  UserEmailSearchResultSearchTermIconCornerDownLeft,
  UserEmailSearchResultSearchTermTypographyNoItem,
  UserEmailSearchResultSearchTermTypographyResult,
} from '../styled/social/SocialUserEmailSearch.styled'

// Phase 2 (search overhaul): the member picker no longer queries the Elastic eaters index directly
// from the browser (no more REACT_APP_ELASTIC_API_KEY / ElasticsearchAPIConnector). It now calls the
// resactly-service `/search/eaters` proxy (REACT_APP_SERVICE_URL) which forwards to resactly-dis, so
// the exposed Elastic key is gone and dis captures the search terms server-side. Behavior (filter
// out self, add existing eater, or add a free-text email) is preserved.

interface SocialUserEmailSearchProps {
  memberList: Member[]
  setMemberList: React.Dispatch<React.SetStateAction<Member[]>>
  type?: 'create' | 'update'
  isEmailValid: boolean
}

const SocialUserEmailSearch: React.FC<SocialUserEmailSearchProps> = ({
  memberList,
  setMemberList,
  type = 'create',
}): JSX.Element => {
  const userDetail = useAppSelector(selectEater)

  const [searchTerm, setSearchTerm] = useState('')
  const [results, setResults] = useState<EaterSearchResult[]>([])
  const requestSeqRef = useRef(0)

  const fetchResults = useMemo(
    () =>
      debounce(
        (term: string, onResults: (options: EaterSearchResult[]) => void) => {
          void (async () => {
            try {
              const options = await fetchEaterSearch({ q: term })
              onResults(options)
            } catch {
              onResults([])
            }
          })()
        },
        1000,
      ),
    [],
  )

  useEffect(() => {
    if (!searchTerm) {
      setResults([])
      return
    }
    const reqId = ++requestSeqRef.current
    fetchResults(searchTerm, (options) => {
      if (reqId !== requestSeqRef.current) return
      setResults(options)
    })
  }, [searchTerm, fetchResults])

  const removeUser = (idx: number): void => {
    setMemberList(memberList.filter((_, index) => index !== idx))
  }

  const addEmail = (user: Member): void => {
    if (
      (memberList.length < 1 &&
        type === 'update' &&
        user &&
        user.eaterId !== userDetail?.eaterId &&
        !memberList.find((member) => member.eaterId === user.eaterId)) ||
      (type === 'create' &&
        user &&
        user.eaterId !== userDetail?.eaterId &&
        !memberList.find((member) => member.eaterId === user.eaterId))
    ) {
      setMemberList([...memberList, user])
    }
  }

  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const reset = (): void => {
    setSearchTerm('')
    setResults([])
  }

  const addFreeTextEmail = (email: string, onDone: () => void): void => {
    if (isValidEmail(email.trim())) {
      addEmail({
        name: email.trim(),
        email: email.trim(),
        phone: '',
        eaterId: '',
      })
      onDone()
    }
  }

  const addExistingValue = (selection: EaterSearchResult): void => {
    addEmail({
      name: `${selection.firstName ?? ''} ${selection.lastName ?? ''}`,
      email: selection.email ?? '',
      phone: selection.phone ?? '',
      eaterId: selection.eaterId ?? '',
    })
    if (type === 'create') reset()
  }

  const data = results.filter(
    (item) => item.eaterId !== userDetail?.eaterId,
  )

  const renderSearch = (): JSX.Element => (
    <OnboardSearchContainer
      customWidth={'386px'}
      style={{
        flexGrow: 1,
        position: 'relative',
      }}
    >
      <div style={{ width: '100%', flexGrow: 1 }}>
        <TextField
          autoFocus
          fullWidth
          value={searchTerm}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setSearchTerm(e.target.value)
          }
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') {
              const value = searchTerm.trim()
              if (value && isValidEmail(value)) {
                addFreeTextEmail(value, () => setSearchTerm(''))
              }
            }
          }}
          sx={{
            flexGrow: 1,
            '&.sui-search-box__text-input': {
              boxShadow: 'none !important',
            },
            '& .MuiInput-root': {
              padding: '5px',
              fontFamily: SANS_FONT,
              fontSize: '16px',
              lineHeight: '16px',
              fontWeight: 500,
            },
          }}
          variant="standard"
          InputProps={{
            disableUnderline: true,
          }}
        />

        {data.length > 0 && (
          <UserEmailAutocompleteViewMainBox className="autocomplete-menu">
            <UserEmailAutocompleteViewSecondBox>
              {data.map((result) => (
                <NavbarSearchAutocompleteViewInnerContainer
                  key={result.eaterId}
                  id="MuiInputBase-input-MuiOutlinedInput-input-check"
                  onClick={() => addExistingValue(result)}
                  style={{
                    width: '100%',
                  }}
                >
                  <UserEmailAutocompleteViewListDetailBox>
                    <UserEmailAutocompleteViewListDetailImage
                      src={result.photo}
                    />
                    <Box pl="10px" sx={{ flex: 1, minWidth: 0 }}>
                      <UserEmailAutocompleteViewListDetailTypography>
                        {`${result.firstName ?? ''} ${result.lastName ?? ''}`}
                      </UserEmailAutocompleteViewListDetailTypography>
                      <UserEmailAutocompleteViewListDetailSubTypography>
                        {result.email}
                      </UserEmailAutocompleteViewListDetailSubTypography>
                      {[
                        result.instagram,
                        result.x,
                        result.tiktok,
                        result.facebook,
                      ].some(Boolean) && (
                        <UserEmailAutocompleteViewListDetailSubTypography
                          sx={{ fontSize: '12px', mt: '2px' }}
                        >
                          {[
                            result.instagram && `@${result.instagram}`,
                            result.x && `@${result.x}`,
                            result.tiktok && `@${result.tiktok}`,
                            result.facebook && `@${result.facebook}`,
                          ]
                            .filter(Boolean)
                            .join(' • ')}
                        </UserEmailAutocompleteViewListDetailSubTypography>
                      )}
                    </Box>
                  </UserEmailAutocompleteViewListDetailBox>
                </NavbarSearchAutocompleteViewInnerContainer>
              ))}
            </UserEmailAutocompleteViewSecondBox>
          </UserEmailAutocompleteViewMainBox>
        )}
      </div>

      {searchTerm &&
        searchTerm.length > 0 &&
        data.length === 0 &&
        isValidEmail(searchTerm.trim()) && (
          <UserEmailSearchResultSearchTermContainer
            customWidth="386px"
            onClick={() => addFreeTextEmail(searchTerm, reset)}
          >
            <UserEmailSearchResultSearchTermIconCornerDownLeft />
            <UserEmailSearchResultSearchTermTypographyNoItem>
              No results found. Click to add &nbsp;
            </UserEmailSearchResultSearchTermTypographyNoItem>
            <UserEmailSearchResultSearchTermTypographyResult>
              {searchTerm}
            </UserEmailSearchResultSearchTermTypographyResult>
          </UserEmailSearchResultSearchTermContainer>
        )}
    </OnboardSearchContainer>
  )

  return (
    <UserEmailOptionDialogMainBox isHeight={type === 'update'}>
      {type === 'create' && (
        <>
          {memberList.map((user, index) => (
            <UserEmailOptionDialogChip
              key={index}
              label={user.email}
              onDelete={() => removeUser(index)}
            />
          ))}
        </>
      )}

      {memberList.length > 0 && type === 'update' && (
        <UserEmailOptionDialogDetailBox>
          <UserEmailOptionDialogDetailTypography>
            {memberList?.[0]?.email}
          </UserEmailOptionDialogDetailTypography>
          <UserEmailOptionDialogDetailIcon
            onClick={() => {
              removeUser(0)
            }}
          />
        </UserEmailOptionDialogDetailBox>
      )}

      {(type === 'create' || (memberList.length <= 0 && type === 'update')) &&
        renderSearch()}
    </UserEmailOptionDialogMainBox>
  )
}

export default React.memo(SocialUserEmailSearch)
